import { Controller } from "@hotwired/stimulus"

// Fills a password + confirmation pair with a strength-conforming
// temporary password, and toggles both fields between hidden and
// visible. Generation stays in the browser so the operator can read
// the value before saving; nothing is sent until the form submits.
//
// The charset and the email-part exclusion match Passwords::Generator
// and User#password_strength (length >= 12, lower, upper, digit,
// special, and no email local-part or domain label).
const LOWER = "abcdefghijklmnopqrstuvwxyz"
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
const DIGITS = "0123456789"
const SPECIALS = "!@#$%^&*?-_+"
const ALL = LOWER + UPPER + DIGITS + SPECIALS
const LENGTH = 16

export default class extends Controller {
  static targets = ["field", "toggle"]

  toggle() {
    const reveal = this.fieldTargets[0]?.type === "password"
    this.fieldTargets.forEach((field) => {
      field.type = reveal ? "text" : "password"
    })
    this.toggleTargets.forEach((button) => {
      button.setAttribute("aria-pressed", reveal ? "true" : "false")
      button.setAttribute("aria-label", reveal ? button.dataset.hideLabel : button.dataset.showLabel)
      const icon = button.querySelector("i")
      if (!icon) return
      icon.classList.toggle("fa-eye", !reveal)
      icon.classList.toggle("fa-eye-slash", reveal)
    })
  }

  generate() {
    const password = this.#conforming(this.#avoidParts())
    this.fieldTargets.forEach((field) => {
      field.value = password
      field.type = "text"
    })
    this.toggleTargets.forEach((button) => {
      button.setAttribute("aria-pressed", "true")
      button.setAttribute("aria-label", button.dataset.hideLabel)
      const icon = button.querySelector("i")
      if (!icon) return
      icon.classList.remove("fa-eye")
      icon.classList.add("fa-eye-slash")
    })
  }

  #avoidParts() {
    const email = this.element.closest("form")?.querySelector("[name='user[email_address]']")?.value || ""
    const [local, domain] = email.split("@")
    return [local, domain?.split(".")[0]]
      .filter((part) => part && part.length > 0)
      .map((part) => part.toLowerCase())
  }

  #conforming(avoid) {
    for (let attempt = 0; attempt < 50; attempt += 1) {
      const candidate = this.#randomPassword()
      const down = candidate.toLowerCase()
      if (!avoid.some((part) => down.includes(part))) return candidate
    }
    return this.#randomPassword()
  }

  #randomPassword() {
    const required = [LOWER, UPPER, DIGITS, SPECIALS].map((set) => this.#pick(set))
    const rest = Array.from({ length: LENGTH - required.length }, () => this.#pick(ALL))
    return this.#shuffle(required.concat(rest)).join("")
  }

  #pick(set) {
    const bytes = new Uint32Array(1)
    crypto.getRandomValues(bytes)
    return set[bytes[0] % set.length]
  }

  #shuffle(chars) {
    const bytes = new Uint32Array(chars.length)
    crypto.getRandomValues(bytes)
    return chars
      .map((char, index) => [bytes[index], char])
      .sort((a, b) => a[0] - b[0])
      .map((pair) => pair[1])
  }
}
